import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Tag, Wallet, CreditCard, Plus, Gift, Smartphone } from "lucide-react";
import { api, apiErr, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export default function Checkout() {
  const { items, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [quote, setQuote] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [method, setMethod] = useState("cod");
  const [payInfo, setPayInfo] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selAddr, setSelAddr] = useState(null);
  const [newAddr, setNewAddr] = useState(false);
  const [form, setForm] = useState({ label: "Home", name: user?.name || "", phone: user?.phone || "", line1: "", line2: "", city: "", state: "", pincode: "" });
  const [placing, setPlacing] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [offers, setOffers] = useState([]);
  const [useRewards, setUseRewards] = useState(false);
  const [redeemPts, setRedeemPts] = useState(0);
  const [agentCode, setAgentCode] = useState("");

  const cartPayload = items.map((i) => ({ product_id: i.product_id, quantity: i.quantity }));

  const refreshQuote = async (code = coupon, m = method, pts = (useRewards ? redeemPts : 0)) => {
    if (!items.length) { setQuote(null); return false; }
    try {
      const { data } = await api.post("/checkout/quote", { items: cartPayload, payment_method: m, coupon_code: code || null, redeem_points: Number(pts) || 0, idempotency_key: "q" });
      setQuote(data);
      return true;
    } catch (e) { toast.error(apiErr(e)); setCoupon(""); refreshQuoteNoCoupon(m); return false; }
  };
  const refreshQuoteNoCoupon = async (m) => {
    const { data } = await api.post("/checkout/quote", { items: cartPayload, payment_method: m, redeem_points: (useRewards ? Number(redeemPts) || 0 : 0), idempotency_key: "q" });
    setQuote(data);
  };

  useEffect(() => { if (user === false) nav("/login?next=/checkout"); }, [user]);
  useEffect(() => { api.get("/payment-info").then((r) => setPayInfo(r.data)).catch(() => {}); }, []);
  useEffect(() => { refreshQuote(); }, [items.length, method, useRewards, redeemPts]);
  useEffect(() => { api.get("/coupons").then((r) => setOffers(r.data)).catch(() => {}); }, []);
  useEffect(() => { const ref = localStorage.getItem("rsmart_referral"); if (ref) setAgentCode(ref); }, []);
  useEffect(() => {
    const pend = localStorage.getItem("rsmart_pending_coupon");
    if (pend && items.length && !coupon) {
      (async () => {
        const okq = await refreshQuote(pend, method);
        if (okq) { setCoupon(pend); toast.success(`Banner coupon ${pend} apply ho gaya`); }
        else localStorage.removeItem("rsmart_pending_coupon"); // invalid/expired — don't re-fire every visit
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);
  useEffect(() => {
    if (user) {
      api.get("/addresses").then((r) => { setAddresses(r.data); const d = r.data.find((a) => a.is_default) || r.data[0]; if (d) setSelAddr(d.id); else setNewAddr(true); });
      api.get("/loyalty/me").then((r) => setLoyalty(r.data)).catch(() => {});
    }
  }, [user]);

  const maxRedeemable = () => {
    if (!loyalty?.config?.enabled) return 0;
    const bal = loyalty.balance || 0;
    const rate = loyalty.config.redeem_value_per_point || 0;
    const cap = ((quote?.total_before_redeem || 0) * (loyalty.config.max_redeem_percent || 0)) / 100;
    const byCap = rate > 0 ? cap / rate : bal;
    return Math.floor(Math.min(bal, byCap) * 100) / 100;
  };

  const toggleRewards = (on) => {
    setUseRewards(on);
    if (on) setRedeemPts(maxRedeemable()); else setRedeemPts(0);
  };

  const saveAddress = async () => {
    if (!form.name || !form.phone || !form.line1 || !form.city || !form.pincode) return toast.error("Fill all address fields");
    const { data } = await api.post("/addresses", { ...form, is_default: addresses.length === 0 });
    setAddresses((p) => [...p.filter((a) => a.id !== data.id), data]); setSelAddr(data.id); setNewAddr(false); toast.success("Address saved");
  };

  const placeOrder = async () => {
    if (!items.length) return;
    const addr = addresses.find((a) => a.id === selAddr);
    if (!addr && !newAddr) return toast.error("Select a delivery address");
    setPlacing(true);
    try {
      const { data } = await api.post("/checkout", {
        items: cartPayload, address: addr || null, payment_method: method,
        coupon_code: quote?.coupon || null, redeem_points: useRewards ? Number(redeemPts) || 0 : 0,
        agent_code: agentCode || null,
        idempotency_key: uuid(), origin_url: window.location.origin,
      });
      localStorage.removeItem("rsmart_pending_coupon");
      localStorage.removeItem("rsmart_referral");
      if (method === "online" && data.checkout_url) { clear(); window.location.href = data.checkout_url; return; }
      clear();
      toast.success(`Order ${data.order_no} placed!${data.points_earned ? ` Earn +${data.points_earned} pts on delivery` : ""}`);
      nav(`/orders/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setPlacing(false); }
  };

  if (!items.length) return <div className="max-w-md mx-auto text-center py-24 text-slate-400" data-testid="empty-checkout">Your cart is empty. <button onClick={() => nav("/")} className="text-emerald-600 font-bold">Shop now</button></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-8 py-6">
      <h1 className="text-2xl font-bold mb-5">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {/* Address */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <h2 className="font-bold flex items-center gap-2 mb-3"><MapPin size={18} className="text-emerald-600" /> Delivery Address</h2>
            <div className="space-y-2">
              {addresses.map((a) => (
                <label key={a.id} data-testid={`address-${a.id}`} className={`flex gap-3 p-3 rounded-xl border cursor-pointer ${selAddr === a.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
                  <input type="radio" checked={selAddr === a.id} onChange={() => { setSelAddr(a.id); setNewAddr(false); }} className="accent-emerald-600 mt-1" />
                  <div className="text-sm"><span className="font-bold">{a.label}</span> · {a.name} · {a.phone}<p className="text-slate-500">{a.line1}, {a.city}, {a.state} - {a.pincode}</p></div>
                </label>
              ))}
            </div>
            {!newAddr ? (
              <button data-testid="add-address-btn" onClick={() => setNewAddr(true)} className="mt-3 flex items-center gap-1 text-emerald-600 font-bold text-sm"><Plus size={15} /> Add new address</button>
            ) : (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {[["name","Full name"],["phone","Phone"],["line1","Address line 1"],["line2","Landmark (optional)"],["city","City"],["state","State"],["pincode","Pincode"]].map(([k,ph]) => (
                  <input key={k} data-testid={`addr-${k}`} placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`border border-slate-200 rounded-lg px-3 py-2 text-sm ${k==='line1'||k==='line2'?'col-span-2':''}`} />
                ))}
                <button data-testid="save-address-btn" onClick={saveAddress} className="col-span-2 bg-emerald-600 text-white rounded-lg py-2 font-bold text-sm">Save Address</button>
              </div>
            )}
          </div>
          {/* Payment */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <h2 className="font-bold mb-3">Payment Method</h2>
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="pay-cod" onClick={() => setMethod("cod")} className={`p-4 rounded-xl border flex items-center gap-2 font-semibold text-sm ${method==="cod"?"border-emerald-500 bg-emerald-50 text-emerald-700":"border-slate-200"}`}><Wallet size={18} /> Cash on Delivery</button>
              <button data-testid="pay-online" onClick={() => setMethod("online")} className={`p-4 rounded-xl border flex items-center gap-2 font-semibold text-sm ${method==="online"?"border-emerald-500 bg-emerald-50 text-emerald-700":"border-slate-200"}`}><CreditCard size={18} /> Pay Online (PayU)</button>
            </div>
            {method === "online" && <p className="text-xs text-slate-400 mt-2">PayU secure checkout — UPI, Cards, NetBanking aur Wallets. Pay karne ke liye aap PayU ke secure page par jaayenge.</p>}
            {payInfo?.upi_id && (
              <div className="mt-3 flex items-center gap-2 bg-violet-50 border border-violet-100 rounded-xl px-3 py-2.5" data-testid="checkout-upi-info">
                <Smartphone size={18} className="text-violet-600 shrink-0" />
                <div className="text-sm"><span className="text-slate-500">UPI se bhi pay kar sakte hain: </span><b className="text-violet-700" data-testid="checkout-upi-id">{payInfo.upi_id}</b></div>
              </div>
            )}
          </div>
        </div>
        {/* Summary */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-3 h-fit">
          <h2 className="font-bold">Order Summary</h2>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {items.map((i) => <div key={i.product_id} className="flex justify-between text-sm"><span className="text-slate-600 line-clamp-1">{i.name} × {i.quantity}</span><span className="font-semibold">{inr(i.price * i.quantity)}</span></div>)}
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1"><Tag size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" /><input data-testid="coupon-input" placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} className="w-full border border-slate-200 rounded-lg pl-8 pr-2 py-2 text-sm" /></div>
            <button data-testid="apply-coupon" onClick={() => refreshQuote(coupon, method)} className="bg-slate-900 text-white rounded-lg px-3 text-sm font-bold">Apply</button>
          </div>
          {offers.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="available-offers">
              {offers.map((o) => (
                <button key={o.code} data-testid={`offer-${o.code}`} onClick={() => { setCoupon(o.code); refreshQuote(o.code, method); }} className="text-[11px] font-bold px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100">
                  {o.code} · {o.type === "percent" ? `${o.value}% off` : `₹${o.value} off`}{o.min_order ? ` (min ₹${o.min_order})` : ""}
                </button>
              ))}
            </div>
          )}

          <div className="relative" data-testid="referral-row">
            <input data-testid="referral-input" placeholder="Agent referral code (optional)" value={agentCode} onChange={(e) => setAgentCode(e.target.value.toUpperCase())} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
          </div>

          {/* Loyalty rewards */}
          {loyalty?.config?.enabled && loyalty.balance > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="loyalty-redeem-box">
              <label className="flex items-center gap-2 text-sm font-bold text-amber-800 cursor-pointer">
                <input type="checkbox" data-testid="use-rewards" checked={useRewards} onChange={(e) => toggleRewards(e.target.checked)} className="accent-amber-600 w-4 h-4" />
                <Gift size={15} /> Use reward points
              </label>
              <p className="text-xs text-amber-700 mt-1">Balance: <b>{loyalty.balance} pts</b> (worth {inr(loyalty.wallet_value)}). Max usable now: {maxRedeemable()} pts.</p>
              {useRewards && (
                <div className="flex gap-2 mt-2">
                  <input data-testid="redeem-points-input" type="number" min="0" step="0.01" max={maxRedeemable()} value={redeemPts} onChange={(e) => setRedeemPts(e.target.value)} className="flex-1 border border-amber-300 rounded-lg px-3 py-1.5 text-sm" />
                  <button data-testid="redeem-max" onClick={() => setRedeemPts(maxRedeemable())} className="bg-amber-500 text-white rounded-lg px-3 text-xs font-bold">Max</button>
                </div>
              )}
            </div>
          )}

          {quote && (
            <div className="space-y-1.5 text-sm border-t border-slate-200 pt-3">
              <Row l="Subtotal" v={inr(quote.subtotal)} />
              {quote.discount > 0 && <Row l={`Discount (${quote.coupon})`} v={`- ${inr(quote.discount)}`} green />}
              <Row l="GST" v={inr(quote.gst)} />
              <Row l="Delivery" v={quote.delivery_fee ? inr(quote.delivery_fee) : "FREE"} green={!quote.delivery_fee} />
              {quote.loyalty_redeem > 0 && <Row l="Reward points" v={`- ${inr(quote.loyalty_redeem)}`} green />}
              <div className="flex justify-between font-extrabold text-lg pt-2 border-t border-slate-200"><span>Total</span><span data-testid="checkout-total">{inr(quote.total)}</span></div>
              {quote.points_earn_preview > 0 && <p className="text-xs text-emerald-600 font-semibold pt-1" data-testid="points-earn-preview">You'll earn {quote.points_earn_preview} reward points when delivered 🎁</p>}
            </div>
          )}
          <button data-testid="place-order-btn" disabled={placing} onClick={placeOrder} className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-xl font-bold transition-colors">
            {placing ? "Placing…" : method === "online" ? "Pay & Place Order" : "Place Order (COD)"}
          </button>
        </div>
      </div>
    </div>
  );
}
const Row = ({ l, v, green }) => <div className="flex justify-between"><span className="text-slate-600">{l}</span><span className={`font-semibold ${green ? "text-emerald-600" : ""}`}>{v}</span></div>;
